// organization.effects.ts
import { Injectable, inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { BlockUI, NgBlockUI } from "ng-block-ui";
import { MessageService } from "primeng/api";
import { catchError, map, mergeMap, of } from "rxjs";
import { RestService } from "../../services/rest.service";
import { ToasterService } from "../../services/toaster-service";
import { MessageEnum } from "../../utils/message.enum";
import { OrganizationActions } from "../actions/organization.actions";

@Injectable()
export class OrganizationEffects {
  @BlockUI() blockUI!: NgBlockUI;
  private messageService = inject(MessageService);

  constructor(private actions$: Actions, private restService: RestService, private readonly toaster: ToasterService) {}

  loadOrganization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.loadOrganization),
      mergeMap(() =>
        this.restService.getOrganization().pipe(
          map((response: any) => {
            this.blockUI.stop();
            const organizationList = response.data.sort((a: any, b: any) => {
              return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
            });
            return OrganizationActions.loadOrganizationSuccess({
              organizationList,
            });
          }),
          catchError((error) => {
            this.blockUI.stop();
            return of(OrganizationActions.loadOrganizationFailure({ error }));
          })
        )
      )
    )
  );

  createOrganization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.createOrganization),
      mergeMap((action) =>
        this.restService.createOrganization(action.organization).pipe(
          map((newOrganization: any) => {
            this.toaster.pop({ type: 'success', text: MessageEnum.CLIENT_CREATE_SUCCESS });
            this.blockUI.stop();
            const organizationData = newOrganization?.data;
            return OrganizationActions.createOrganizationSuccess({
              organization: organizationData,
            });
          }),
          catchError((error) => {
            this.blockUI.stop();
            this.toaster.pop({ type: 'error', text: MessageEnum.CLIENT_CREATE_FAILURE });
            return of(OrganizationActions.createOrganizationFailure({ error }));
          })
        )
      )
    )
  );

  editOrganization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.editOrganization),
      mergeMap((action) =>
        this.restService.updateOrganization(action.organization).pipe(
          map((updatedOrganization: any) => {
            this.blockUI.stop();
            this.toaster.pop({ type: 'success', text: MessageEnum.CLIENT_UPDATE_SUCCESS });
            // PATCH may return the org directly or wrapped in {data:...}; fall back to
            // the original request payload so the reducer always receives a valid object.
            const org = updatedOrganization?.data ?? updatedOrganization ?? action.organization;
            return OrganizationActions.editOrganizationSuccess({
              organization: org,
            });
          }),
          catchError((error) => {
            this.blockUI.stop();
            this.toaster.pop({ type: 'error', text: MessageEnum.CLIENT_UPDATE_FAILURE });
            return of(OrganizationActions.loadOrganizationFailure({ error }));
          })
        )
      )
    )
  );

  deleteOrganization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationActions.deleteOrganization),
      mergeMap((action) =>
        this.restService.deleteOrganization(action.organizationId).pipe(
          map(() => {
            this.blockUI.stop();
            this.toaster.pop({ type: 'success', text: MessageEnum.CLIENT_DELETE_SUCCESS });
            return OrganizationActions.deleteOrganizationSuccess({
              organizationId: action.organizationId,
            });
          }),
          catchError((error) => {
            this.blockUI.stop();
            this.toaster.pop({ type: 'error', text: MessageEnum.CLIENT_DELETE_FAILURE });
            return of(OrganizationActions.loadOrganizationFailure({ error }));
          })
        )
      )
    )
  );
}
